// src/features/analytics/components/AnalyticsDeliveryPage.tsx
//
// CDN DELIVERY view. Customer-facing language is always "CDN delivery" /
// "delivery", never viewership (ref_analytics_cdn_content_semantics.md).

import { useState } from 'react';
import {
  Badge,
  Group,
  Paper,
  Skeleton,
  Stack,
  Switch,
  Table,
  Text,
  Tooltip,
} from '@mantine/core';
import { BarChart } from '@mantine/charts';
import { DateTime } from 'luxon';
import { AnalyticsUtils } from '../AnalyticsUtils';
import { useAnalyticsRange } from '../useAnalyticsRange';
import { useAnalyticsScope } from '../useAnalyticsScope';
import { useAnalyticsTransfer } from '../useAnalyticsTransfer';
import { AnalyticsCoverageNotice } from './AnalyticsCoverageNotice';
import { AnalyticsErrorState } from './AnalyticsErrorState';
import type { TransferRow } from '../types';

/** Forward-only CDN collection floor (technical boundary, not the product window T). */
const CDN_DATA_FLOOR_NOTE = 'CDN delivery data begins May 29, 2026 (UTC).';

const PROVISIONAL_TOOLTIP = 'Provisional — may change for ~3 days until finalization.';

const SERIES_COLORS = ['blue.6', 'cyan.5', 'teal.5', 'indigo.5', 'grape.5', 'orange.5'] as const;

function utcDay(seconds: number): string {
  return DateTime.fromSeconds(seconds, { zone: 'utc' }).toFormat('MMM d');
}

interface ChartDatum {
  day: string;
  [trafficClass: string]: string | number;
}

function buildChart(rows: TransferRow[]): { data: ChartDatum[]; classes: string[] } {
  const classes = [...new Set(rows.map((r) => r.trafficClass))].sort();
  const byDay = new Map<number, ChartDatum>();
  for (const row of rows) {
    let datum = byDay.get(row.day);
    if (!datum) {
      datum = { day: utcDay(row.day) };
      byDay.set(row.day, datum);
    }
    datum[row.trafficClass] = ((datum[row.trafficClass] as number) ?? 0) + row.bytesTransferred;
  }
  const data = [...byDay.entries()].sort(([a], [b]) => a - b).map(([, datum]) => datum);
  return { data, classes };
}

export function AnalyticsDeliveryPage() {
  const { search, range } = useAnalyticsRange();
  const { scope } = useAnalyticsScope(search.scope);
  const [finalizedOnly, setFinalizedOnly] = useState(false);

  const { transfer, isLoading, isError, error, refetch } = useAnalyticsTransfer(
    scope,
    range,
    finalizedOnly
  );

  const rows = transfer?.data.rows ?? [];
  const { data: chartData, classes } = buildChart(rows);

  return (
    <Stack gap="md">
      <Group justify="space-between" wrap="wrap">
        <Text size="xs" c="dimmed">
          {CDN_DATA_FLOOR_NOTE} Day boundaries are UTC.
        </Text>
        <Switch
          size="sm"
          label="Finalized only"
          checked={finalizedOnly}
          onChange={(event) => setFinalizedOnly(event.currentTarget.checked)}
        />
      </Group>

      {isError ? (
        <AnalyticsErrorState error={error} onRetry={refetch} />
      ) : isLoading ? (
        <Skeleton height={260} />
      ) : rows.length === 0 ? (
        <AnalyticsCoverageNotice
          coverage={transfer?.coverage}
          freshness={transfer?.freshness}
          emptyMessage="No CDN delivery data for this range."
        />
      ) : (
        <>
          <AnalyticsCoverageNotice coverage={transfer?.coverage} freshness={transfer?.freshness} />
          <Paper withBorder p="md">
            <BarChart
              h={220}
              data={chartData}
              dataKey="day"
              type="stacked"
              series={classes.map((trafficClass, index) => ({
                name: trafficClass,
                color: SERIES_COLORS[index % SERIES_COLORS.length],
              }))}
              valueFormatter={(value) => AnalyticsUtils.formatBytes(value)}
              withLegend
            />
            <Text size="xs" c="dimmed" mt={4}>
              CDN delivery by day (UTC) and traffic class.
            </Text>
          </Paper>

          <Paper withBorder>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Day (UTC)</Table.Th>
                  <Table.Th>Traffic class</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>Delivered</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>Requests</Table.Th>
                  <Table.Th>Status</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((row) => (
                  <Table.Tr key={`${row.day}-${row.cid}-${row.trafficClass}`}>
                    <Table.Td>{utcDay(row.day)}</Table.Td>
                    <Table.Td>{row.trafficClass}</Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>
                      {AnalyticsUtils.formatBytes(row.bytesTransferred)}
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>
                      {AnalyticsUtils.formatCount(row.requestCount)}
                    </Table.Td>
                    <Table.Td>
                      {row.finalized ? (
                        <Badge color="green" variant="light">
                          Final
                        </Badge>
                      ) : (
                        <Tooltip label={PROVISIONAL_TOOLTIP} withArrow>
                          <Badge color="yellow" variant="light">
                            Provisional
                          </Badge>
                        </Tooltip>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
        </>
      )}
    </Stack>
  );
}
